/**
 * API client with MSAL token acquisition.
 *
 * Wraps fetch() to automatically acquire an Azure AD token
 * and attach it as a Bearer header for backend API calls.
 *
 * Dev:  empty base — Vite proxy forwards /api/* to nexus-core.
 * Prod: set VITE_NEXUS_CORE_BASE_URL in the deploy workflow env: block;
 *       it gets baked into the bundle at build time.
 */

const API_BASE = import.meta.env.DEV ? "" : import.meta.env.VITE_NEXUS_CORE_BASE_URL;
if (!import.meta.env.DEV && !API_BASE) {
  // eslint-disable-next-line no-console
  console.error(
    "[nexus-webui] VITE_NEXUS_CORE_BASE_URL is not set. " +
    "Configure it in the GitHub workflow env: block and redeploy."
  );
}

/**
 * Check if a JWT's exp claim is expired or within a safety buffer.
 * No crypto needed — backend validates the signature.
 */
function isTokenExpired(token, bufferSeconds = 300) {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload.exp <= Math.floor(Date.now() / 1000) + bufferSeconds;
  } catch {
    return true;
  }
}

/**
 * Acquire a valid idToken, with silent → forceRefresh → popup fallback chain.
 * Checks idToken expiry before returning (MSAL only tracks accessToken expiry).
 */
export async function getToken(msalInstance) {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error("Not authenticated");

  const request = { scopes: ["User.Read"], account: accounts[0] };

  // 1. Try silent (from cache) — only use if idToken is still fresh
  try {
    const resp = await msalInstance.acquireTokenSilent(request);
    if (!isTokenExpired(resp.idToken)) return resp.idToken;
  } catch {
    // cache miss or error — continue
  }

  // 2. Force refresh from Azure AD
  try {
    const resp = await msalInstance.acquireTokenSilent({
      ...request,
      forceRefresh: true,
    });
    if (!isTokenExpired(resp.idToken)) return resp.idToken;
  } catch {
    // refresh token failed — continue
  }

  // 3. Interactive popup as last resort
  try {
    const resp = await msalInstance.acquireTokenPopup(request);
    return resp.idToken;
  } catch {
    throw new Error("Session expired. Please sign in again.");
  }
}

/**
 * Fetch from the backend API with automatic MSAL auth.
 * Retries once with a fresh token on 401.
 */
/**
 * One request, with the 401 -> fresh-token -> retry dance done once.
 *
 * apiFetch and apiPost both go through here so the retry logic exists in a
 * single place; it used to live inline in apiFetch, and a second copy in a
 * POST helper is exactly how the two drift apart.
 */
async function apiRequest(msalInstance, path, { method = "GET", body } = {}) {
  const token = await getToken(msalInstance);

  const opts = (t) => {
    const headers = { Authorization: `Bearer ${t}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    return { method, headers, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) };
  };

  let result = await fetch(`${API_BASE}${path}`, opts(token));

  if (result.status === 401) {
    const accounts = msalInstance.getAllAccounts();
    if (!accounts.length) throw new Error("Not authenticated");

    let freshToken;
    try {
      const resp = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0],
        forceRefresh: true,
      });
      freshToken = resp.idToken;
    } catch {
      try {
        const resp = await msalInstance.acquireTokenPopup({
          scopes: ["User.Read"],
          account: accounts[0],
        });
        freshToken = resp.idToken;
      } catch {
        throw new Error("Session expired. Please sign in again.");
      }
    }
    result = await fetch(`${API_BASE}${path}`, opts(freshToken));
  }

  if (!result.ok) throw new Error(`API error ${result.status}`);
  // 204 and empty bodies are legitimate answers to a POST.
  const text = await result.text();
  return text ? JSON.parse(text) : {};
}

export async function apiFetch(msalInstance, path) {
  return apiRequest(msalInstance, path);
}

export async function apiPost(msalInstance, path, body) {
  return apiRequest(msalInstance, path, { method: "POST", body: body ?? {} });
}
